import api from 'Plugins/api'
import TrainOrder, { Passenger } from '../entities/train-order'
import TrainRefundOrder from '../entities/train-refund-order'
import TrainChangeOrder from '../entities/train-change-order'

/**
 * 火车票订单用例类
 */
class TrainOrderInteractor {
  /**
   * 获取火车票正常订单详请
   */
  static async getDetailById(id) {
    try {
      const { data = {} } = await api['trOrder/detail']({ id })
      
      return {
        data: new TrainOrder(data)
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 获取火车票退票订单详请
   */
  static async getRefundDetailById(id) {
    try {
      const { data = {} } = await api['trOrder/refundDetail']({ id })
      
      return {
        data: new TrainRefundOrder(data)
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 获取火车票改签订单详请
   */
  static async getChangeDetailById(id) {
    try {
      const { data = {} } = await api['trOrder/changeDetail']({ changeId: id })
      
      return {
        data: new TrainChangeOrder(data)
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 根据改签订单id或原订单orderId获取退订单集合
   * @param {String} id
   */
  static async getChangeListByOrderId(id) {
    try {
      const { data = [] } = await api['trOrder/findTrChangeOrderByOrderId']({ orderId: id })
      
      return {
        data: data.map(order => new TrainChangeOrder(order))
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 根据改签订单id或原订单orderId获取退订单集合
   * @param {String} ids 多个id逗号 (',') 分隔
   * @param {String} queryType 查询类型 传入'normal' 'refund' 'change' ，默认 'normal'
   */
  static async getRefundListByOrderId(ids, queryType = 'normal') {
    try {
      const instance = {
        normal: { orderId: ids },
        refund: { ids },
        change: { changeId: ids }
      }
      
      const { data = [] } = await api['trOrder/findTrRefundOrderByIds'](instance[queryType])
      
      return {
        data: data.map(order => new TrainRefundOrder(order))
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 获取改签进度
   */
  static async getChangeProgress(id) {
    try {
      const { data = {} } = await api['approval/vipOrderAppProgress']({ 
        orderId: id,
        orderType: $dict.ORDER_TYPE.tr.change
      })
      
      return {
        data
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 获取可退票乘车人
   * @param {String} id 订单id
   * @param {applyType} applyType 申请类型1为改签单申请,其他为正常单申请
   */
  static async getRefundPsgList(id, applyType = '') {
    try {
      const { data = {} } = await api['trOrder/applyTrRefund']({
        orderId: id,
        applyType
      })
      
      data.psgList = data.ticketList.map(psg => new Passenger(psg))
      
      return {
        data
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 乘车人申请退票
   * @param {Object} payload 申请退票参数
   *   ObtSaveTrRefundRequest {
   *    orderFrom (string, optional): 订单来源 10504安卓终端 10505苹果终端 10503企业端网站 ,
   *    orderId (string, optional): 订单id ,
   *    refundReasonVip (string, optional): 企业退票原因 ,
   *    ticketList (Array[ObtRefundTicket], optional): 火车票集合
   *   }
   *   ObtRefundTicket {
   *     ticketId (string, optional): 票id ,
   *     ticketType (string, optional): 票类型1正常票 2改签票
   *   }
   */
  static async submitRefund (payload) {
    try {
      if (payload.ticketList.length === 0) throw '请选择需要退票的乘客！'
      if (!payload.refundReasonVip) throw '请选择企业退票原因！'

      const { data = '' } = await api['trOrder/saveTrRefund'](payload)

      return {
        ids: data.split(',')
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 获取可改签乘车人
   */
  static async getChangePsgList(id) {
    try {
      const { data = {} } = await api['trOrder/applyTrChange']({
        orderId: id
      })
      
      data.psgList = data.changePsgList
      
      return {
        data: new TrainOrder(data)
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * 乘车人申请火车票改签
   * @param {Object} payload 申请改签参数
   * ObtTrChangeOrderAddRequest {
   *   acceptNoseat (string): 是否接受无座 0不接受 1接受 ,
   *   changeProveUrl (string): 改签证明路径 ,
   *   changeReasonVip (string): 企业改签原因 ,
   *   chooseSeats (string, optional): 选座 范围:1A-1F 2A-2F 几个人就几个(一个人时,不允许出现2A-2F) ,
   *   newFromDate (string): 新出发日期不能为空 ,
   *   newFromStationCode (string): 新出发站三字码不能为空 ,
   *   newFromTime (string): 新出发时刻不能为空 ,
   *   newSeatType (string): 新席别不能为空 ,
   *   newTicketPrice (number): 新票价不能为空 ,
   *   newToDate (string): 新到达日期不能为空 ,
   *   newToStationCode (string): 新到达三字码不能为空 ,
   *   newToTime (string): 新到达时间不能为空 ,
   *   newTrainCode (string): 新车次不能为空 ,
   *   orderFrom (string): 订单来源不能为空 ,
   *   orderId (string): 订单id不能为空 ,
   *   password (string): 12306密码 ,
   *   psgIds (string): 需改签乘车人id 多个以,隔开 不能为空 ,
   *   trainAccount (string): 12306账号
   * }
   */
  static async submitChange(payload) {
    try {
      if (payload.psgIds === '') throw '请选择需要改签的乘客！'
      if (!payload.newTrainNo) throw '请查询需要改签的车次！'
      if (!payload.changeReasonVip) throw '请选择企业改签原因！'
  
      const { data = {} } = await api['trOrder/trChangeOrderAdd'](payload)
      
      return {
        ids: data.changeId.split(',')
      }
    } catch (error) {
      throw error
    }
  }


}

export default TrainOrderInteractor